import React, { memo, useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { createUseStyles, FieldGroup, Select } from '@Sage/hoi-poi-ui';
import { useWeb5Context } from 'lib/Web5Context';
import { useDebounce } from 'hooks';
import {
    getRelatedEntities,
    getRelatedEntitiesReverse,
} from 'shards/ForceBI/components/Crud/config';
import styles from './styles';

const useStyles = createUseStyles(styles, 'SelectWithEntity');

// Specific field for XAxis and xAxisBreakdown
const SelectorWithEntity = memo(({ id, onChange, data, label, subFields, isRequired, error }) => {
    const classes = useStyles();
    const { t } = useWeb5Context();

    const isFirstRender = useRef(true);
    const prevEntity = useRef(null);
    const [options, setOptions] = useState([]);
    const [filteredEntities, setFilteredEntities] = useState([]);
    const [isLoadingOptions, setIsLoadingOptions] = useState(false);

    const entities = useMemo(() => {
        return subFields[0].loadCustomOptions({ t });
    }, [t, subFields]);

    const mappedEntities = useMemo(() => {
        return entities.reduce((acc, entity) => {
            acc[entity.value] = entity;
            return acc;
        }, {});
    }, [entities]);

    const getEntityNameFromValue = useCallback((value) => {
        return value?.split?.('.')?.[0] || '';
    }, []);

    const getEntityName = useCallback(
        (options) => {
            let entityName = getEntityNameFromValue(options?.[0]?.value);
            const relatedEntitiesReverse = getRelatedEntitiesReverse();
            if (relatedEntitiesReverse[entityName]) {
                return relatedEntitiesReverse[entityName];
            } else return entityName;
        },
        [getEntityNameFromValue],
    );

    // Define the entityOptions based on the entities existing in the second selector
    const getFilteredEntities = useCallback(
        (options) => {
            if (!entities?.length) return [];
            let newFilteredEntities = [];
            options.forEach((option) => {
                const entityName = getEntityName(option.options);
                newFilteredEntities.push(mappedEntities[entityName]);
            });
            setFilteredEntities(newFilteredEntities);
            return newFilteredEntities;
        },
        [entities, mappedEntities, getEntityName],
    );

    const fetchOptions = useCallback(
        (parentValue) => {
            setIsLoadingOptions(true);
            subFields[1]
                .loadOptions(parentValue)()
                .then((newOptions) => {
                    setOptions(newOptions);
                    getFilteredEntities(newOptions);
                })
                .catch(() => {
                    setOptions([]);
                    setFilteredEntities([]);
                })
                .finally(() => setIsLoadingOptions(false));
        },
        [subFields, getFilteredEntities],
    );

    const debouncedFetchOptions = useDebounce(fetchOptions, 300);

    useEffect(() => {
        if (isFirstRender.current && data?.entity?.value) {
            // Get Options for both selectors in the first render
            isFirstRender.current = false;
            if (data?.entity?.value) {
                const parentValue = data[subFields[1].parentId].value.toLowerCase();
                debouncedFetchOptions(parentValue);
            }
        } else {
            // Get options and entity in any other render
            if (prevEntity.current !== data?.entity?.value && data?.entity?.value) {
                setOptions([]);
                setFilteredEntities([]);
                prevEntity.current = data?.entity?.value;

                const parentValue = data[subFields[1].parentId].value.toLowerCase();
                debouncedFetchOptions(parentValue);
            } else if (!data?.entity?.value) {
                setOptions([]);
                setFilteredEntities([]);
            }
        }
    }, [data, debouncedFetchOptions, subFields]);

    const filteredOptions = useMemo(() => {
        const entity = data?.[subFields[0].id];
        if (!entity?.value) return [];
        const entityValue = entity?.value?.toLowerCase?.();

        const relatedEntities = getRelatedEntities();
        return options.reduce((arr, current) => {
            current.options.forEach((option) => {
                const optionEntityValue = option.value.toLowerCase().split('.')[0];
                if (
                    optionEntityValue === entityValue ||
                    relatedEntities[entityValue]?.includes(optionEntityValue)
                ) {
                    arr.push(option);
                }
            });
            return arr;
        }, []);
    }, [options, subFields, data]);

    const propsWithoutLoadOptions = useMemo(() => {
        const { loadOptions, ...rest } = subFields[1];
        return rest;
    }, [subFields]);

    const inputProps = useMemo(() => {
        return [
            {
                isFullWidth: true,
                ...subFields[0],
                onChange: onChange({ fieldId: subFields[0].id }),
                value: data?.[subFields[0].id],
                options: filteredEntities,
                isLoading: isLoadingOptions,
                isReadOnly: subFields[0].getIsReadOnly({ fieldId: subFields[0].id, data }),
            },
            {
                isFullWidth: true,
                ...propsWithoutLoadOptions,
                value: data?.[subFields[1].id],
                onChange: onChange({ fieldId: subFields[1].id }),
                options: filteredOptions,
                isReadOnly: subFields[1].getIsReadOnly({ fieldId: subFields[1].id, data }),
                isLoading: isLoadingOptions,
            },
        ];
    }, [
        filteredEntities,
        isLoadingOptions,
        propsWithoutLoadOptions,
        subFields,
        data,
        onChange,
        filteredOptions,
    ]);

    return (
        <div className={classes.root}>
            <FieldGroup
                label={label}
                labelMode="vertical"
                fieldsMode="vertical"
                inputs={[Select, Select]}
                isFullWidth={true}
                isRequired={isRequired}
                error={error}
                inputProps={inputProps}
            />
        </div>
    );
});

export default SelectorWithEntity;
